import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';

/**
 * useCgTable — the one calling format for a server-paginated <CgTable/>.
 *
 * Plan: docs/features/cg-table-hook/. The hook owns filters, pagination, the
 * optional URL sync and — the reason it exists — the `loading` derivation, so
 * every table dims/skeletons on a page or filter change instead of each call
 * site inventing its own rule (some pass `isLoading` only and show nothing on
 * page change; others pass `isLoading || isFetching` and blank the table on
 * every unrelated invalidation).
 *
 * The caller's react-query hook is passed IN as `useData` and is called here on
 * every render, unconditionally — that is what keeps filters and pagination
 * from drifting out of the query key.
 *
 *   const { tableProps, setFilters } = useCgTable({
 *     key: 'suppliers',
 *     filters: { name: '', country: '', goods: [] },
 *     useData: ({ filters, pagination }) => useSearchSuppliers({ form: filters, pagination }),
 *     select: (d) => ({ rows: d?.suppliers ?? [], total: d?.itemCount ?? 0 }),
 *     headers,
 *     mapRow: (supplier) => ({ name: <NameCell supplier={supplier} /> }),
 *     syncUrl: true,
 *     busy: deleteMutation.isPending,
 *   });
 *
 *   <CgTable {...tableProps} actions={actions} selectable />
 */

// Internal, non-filter keys the search forms attach to their payloads. They
// drive local behaviour (debounce / preset-load bookkeeping) and must never
// reach the query or the URL.
const isInternalKey = (key) => key.startsWith('_');

const stripInternal = (obj) =>
  Object.fromEntries(Object.entries(obj || {}).filter(([key]) => !isInternalKey(key)));

const isEmptyValue = (value) =>
  value === '' ||
  value === null ||
  value === undefined ||
  (Array.isArray(value) && value.length === 0);

const sameValue = (a, b) => {
  if (Array.isArray(a) || Array.isArray(b)) {
    const left = Array.isArray(a) ? a : [];
    const right = Array.isArray(b) ? b : [];
    return left.length === right.length && left.every((item, i) => item === right[i]);
  }
  return a === b;
};

// URL codec. Value types are inferred from the `filters` defaults rather than
// configured per field: array ⇒ CSV, number ⇒ int, boolean ⇒ 'true', else string.
const parseParam = (raw, defaultValue) => {
  if (Array.isArray(defaultValue)) return raw.split(',').filter(Boolean);
  if (typeof defaultValue === 'number') {
    const parsed = parseInt(raw, 10);
    return Number.isNaN(parsed) ? defaultValue : parsed;
  }
  if (typeof defaultValue === 'boolean') return raw === 'true';
  return raw;
};

const readFiltersFromSearch = (search, defaults) => {
  const params = new URLSearchParams(search);
  const result = {};
  Object.entries(defaults).forEach(([key, defaultValue]) => {
    const raw = params.get(key);
    result[key] = raw === null ? defaultValue : parseParam(raw, defaultValue);
  });
  return result;
};

const writeSearch = (search, filters, defaults, page, limit) => {
  const params = new URLSearchParams(search);
  Object.entries(defaults).forEach(([key, defaultValue]) => {
    const value = filters[key];
    // A value that equals its default carries no information — keep it out of
    // the URL so a reset produces a clean address bar.
    if (isEmptyValue(value) || sameValue(value, defaultValue)) {
      params.delete(key);
    } else if (Array.isArray(value)) {
      params.set(key, value.join(','));
    } else {
      params.set(key, String(value));
    }
  });
  params.set('page', String(page));
  params.set('limit', String(limit));
  return params.toString();
};

const defaultSelect = (data) => {
  if (Array.isArray(data)) return { rows: data, total: data.length };
  return { rows: data?.rows ?? [], total: data?.total ?? 0 };
};

export const useCgTable = ({
  key,
  filters: filterDefaults = {},
  useData,
  select,
  headers,
  mapRow,
  syncUrl = false,
  defaultLimit = 10,
  busy = false,
  clientPaginate = false,
}) => {
  if (typeof useData !== 'function') {
    throw new Error('useCgTable: `useData` must be a hook of ({ filters, pagination }).');
  }

  const history = useHistory();
  const location = useLocation();

  // Defaults are captured once: they define the filter shape (and therefore the
  // URL codec and what "reset" means), so a caller re-creating the object
  // literal each render must not reshuffle state.
  const defaultsRef = useRef(filterDefaults);
  const defaults = defaultsRef.current;

  const [initialState] = useState(() => {
    if (!syncUrl) {
      return { filters: { ...defaults }, page: 1, limit: defaultLimit };
    }
    const params = new URLSearchParams(location.search);
    return {
      filters: readFiltersFromSearch(location.search, defaults),
      page: parseInt(params.get('page'), 10) || 1,
      limit: parseInt(params.get('limit'), 10) || defaultLimit,
    };
  });

  // `draft` is what the filter form shows; `committed` is what the query and
  // the URL use. They diverge only while a debounced text input is being typed
  // into (payloads flagged `_isTyping`).
  const [draft, setDraft] = useState(initialState.filters);
  const [committed, setCommitted] = useState(initialState.filters);
  const [page, setPage] = useState(initialState.page);
  const [limit, setLimit] = useState(initialState.limit);

  const draftRef = useRef(draft);
  draftRef.current = draft;

  const applyPayload = useCallback((next) => {
    const value = typeof next === 'function' ? next(draftRef.current) : next;
    if (!value) return;

    // CgTable's column filters merge `page: 1` into the payload they emit
    // (Table.jsx handleColumnFilterChange), and preset loads can carry a
    // `limit`. Route those to pagination instead of letting them ride along
    // into the query's filter object.
    const { page: nextPage, limit: nextLimit, ...rest } = value;

    const isTyping = Boolean(rest._isTyping);

    // Merged onto the defaults so a partial payload (the reset shapes some
    // forms emit omit half their fields) can never leave a filter `undefined`
    // — which would read as "not the default", i.e. permanently filtered.
    //
    // `_isTyping` is consumed here and NOT stored: the draft is handed back to
    // the form as `form`, and forms routinely build their next payload as
    // `{ ...form, field: value }`. Persisting the flag would make it ride along
    // into the following commit, which would then be read as more typing and
    // the query would never update again.
    const nextDraft = { ...defaultsRef.current, ...rest };
    delete nextDraft._isTyping;
    setDraft(nextDraft);
    if (isTyping) return; // draft only — no query, no URL

    setCommitted({ ...defaultsRef.current, ...stripInternal(rest) });
    if (nextLimit) setLimit(nextLimit);
    // Any committed filter change lands the user back on page 1; an explicit
    // page in the payload still wins.
    setPage(nextPage || 1);
  }, []);

  const handlePageChange = useCallback((nextPage, nextLimit) => {
    if (nextLimit) {
      setLimit(nextLimit);
      setPage(1); // a new page size invalidates the current offset
      return;
    }
    setPage(nextPage);
  }, []);

  const resetFilters = useCallback(() => {
    setDraft({ ...defaults });
    setCommitted({ ...defaults });
    setPage(1);
  }, [defaults]);

  // ── The query ──
  const queryFilters = useMemo(() => stripInternal(committed), [committed]);
  const paginationInput = useMemo(() => ({ page, limit }), [page, limit]);

  const query = useData({ filters: queryFilters, pagination: paginationInput }) || {};
  const { data, isLoading, isFetching, isPlaceholderData } = query;

  // ── URL sync ──
  // One ref holds the query string both effects agree is "in sync". It starts
  // null so the FIRST run adopts whatever the URL already said instead of
  // rewriting it — a page load must not replace the address bar just to add
  // `page=1&limit=10`.
  const syncedSearchRef = useRef(null);
  const search = syncUrl ? writeSearch(location.search, committed, defaults, page, limit) : '';

  useEffect(() => {
    if (!syncUrl) return;
    const current = location.search.replace(/^\?/, '');
    if (syncedSearchRef.current === null) {
      syncedSearchRef.current = current;
      return;
    }
    if (search === syncedSearchRef.current) return; // our state didn't move
    syncedSearchRef.current = search;
    if (search !== current) history.replace({ pathname: location.pathname, search });
  }, [syncUrl, search, history, location.pathname, location.search]);

  // Back/forward (and any other external navigation) re-hydrates state. Guarded
  // by the same ref so it can never loop against our own writes.
  useEffect(() => {
    if (!syncUrl) return;
    const current = location.search.replace(/^\?/, '');
    if (syncedSearchRef.current === null || current === syncedSearchRef.current) return;
    syncedSearchRef.current = current;
    const params = new URLSearchParams(location.search);
    setDraft(readFiltersFromSearch(location.search, defaults));
    setCommitted(readFiltersFromSearch(location.search, defaults));
    setPage(parseInt(params.get('page'), 10) || 1);
    setLimit(parseInt(params.get('limit'), 10) || defaultLimit);
  }, [syncUrl, location.search, defaults, defaultLimit]);

  // ── Loading (the whole point) ──
  // True when the table is showing something other than the answer to the
  // question currently on screen. A refetch of the SAME params (window focus,
  // an unrelated invalidateQueries) is deliberately not loading — dimming the
  // table under the user for those is what the old `isLoading || isFetching`
  // idiom got wrong.
  // Under clientPaginate the page/limit never reach a request — the caller's
  // query fetches the whole set once and the hook slices it. Leaving page/limit
  // in the key would mark the table pending against a fetch that is never
  // coming, so it would dim on a page click and never un-dim (D9).
  const paramsKey = useMemo(
    () => JSON.stringify(clientPaginate ? [queryFilters] : [queryFilters, page, limit]),
    [clientPaginate, queryFilters, page, limit],
  );
  const settledKeyRef = useRef(paramsKey);
  useEffect(() => {
    if (!isFetching) settledKeyRef.current = paramsKey;
  }, [isFetching, paramsKey]);

  const paramsPending = settledKeyRef.current !== paramsKey;
  const loading = Boolean(
    isLoading || busy || (isFetching && (paramsPending || isPlaceholderData)),
  );

  // ── Rows ──
  const { rows, total } = useMemo(
    () => (typeof select === 'function' ? select(data) : defaultSelect(data)),
    [select, data],
  );
  // CgTable keys rows, selection and row actions off `values[i]._id`, so the
  // hook guarantees it — an endpoint returning `id` gets it copied across.
  // Identity is preserved when `_id` is already there (the common case), which
  // is what keeps the mapRow memo below from re-running on every render.
  const allRows = useMemo(() => {
    const list = Array.isArray(rows) ? rows : [];
    if (list.every((row) => row?._id !== undefined)) return list;
    return list.map((row) => (row?._id !== undefined ? row : { ...row, _id: row?.id }));
  }, [rows]);

  // clientPaginate (D9): `useData` handed back the whole (already-filtered) set,
  // so the visible window is derived here rather than requested. The slice
  // happens BEFORE mapRow so `data` and `values` stay index-aligned (D6), and
  // `total` counts the UNSLICED set so the pagination strip sizes its page list
  // correctly.
  const safeRows = useMemo(() => {
    if (!clientPaginate) return allRows;
    const start = (page - 1) * limit;
    return allRows.slice(start, start + limit);
  }, [clientPaginate, allRows, page, limit]);

  const resolvedTotal = clientPaginate ? allRows.length : (total ?? 0);

  // `data` (display cells) and `values` (raw rows) must stay index-aligned —
  // CgTable indexes one by the other for row keys, selection and row actions.
  const mappedRows = useMemo(() => {
    if (typeof mapRow !== 'function') return safeRows;
    return safeRows.map((row, index) => {
      const mapped = mapRow(row, index) || {};
      return { _id: mapped._id ?? row?._id ?? row?.id, ...mapped };
    });
  }, [safeRows, mapRow]);

  const isFiltered = useMemo(
    () => Object.entries(defaults).some(([k, v]) => !sameValue(queryFilters[k], v)),
    [queryFilters, defaults],
  );

  const pagination = useMemo(
    () => ({ page, limit, total: resolvedTotal, onPageChange: handlePageChange }),
    [page, limit, resolvedTotal, handlePageChange],
  );

  const tableProps = useMemo(
    () => ({
      tableKey: key,
      headers,
      data: mappedRows,
      values: safeRows,
      pagination,
      loading,
      isFiltered,
      onClearFilters: resetFilters,
    }),
    [key, headers, mappedRows, safeRows, pagination, loading, isFiltered, resetFilters],
  );

  return {
    tableProps,
    // Draft filters — bind these to the search form so typing stays responsive.
    filters: draft,
    setFilters: applyPayload,
    resetFilters,
    // Committed filters — what the query and URL actually reflect.
    committedFilters: queryFilters,
    pagination,
    setPage,
    setLimit,
    rows: safeRows,
    data,
    query,
    loading,
    isFiltered,
  };
};

export default useCgTable;
